import { BigNumber } from '@ethersproject/bignumber'
import { formatUnits } from '@ethersproject/units'
import { Currency } from './Currency'
import { bigNumberFromString } from '../utils'

export class CurrencyValue {
  constructor(readonly currency: Currency, readonly value: BigNumber) {}

  static fromString(currency: Currency, value: string) {
    return new CurrencyValue(currency, bigNumberFromString(currency.decimals, value))
  }

  toString() {
    const result = formatUnits(this.value, this.currency.decimals)
    if (result.endsWith('.0')) {
      return result.substring(0, result.length - 2)
    }
    return result
  }

  format(precision?: number) {
    return formatCurrency(this.currency.decimals, this.value, precision)
  }

  formatWithSymbol(precision?: number) {
    return `${formatCurrency(this.currency.decimals, this.value, precision)} ${this.currency.symbol}`
  }

  lt(currencyValue: CurrencyValue) {
    if (this.currency !== currencyValue.currency) {
      throw new Error('Cannot compare different currencies')
    }
    return this.value.lt(currencyValue.value)
  }

  add(currencyValue: CurrencyValue) {
    if (this.currency !== currencyValue.currency) {
      throw new Error('Cannot compare different currencies')
    }
    return new CurrencyValue(this.currency, this.value.add(currencyValue.value))
  }

  map(fn: (value: BigNumber) => BigNumber) {
    return new CurrencyValue(this.currency, fn(this.value))
  }
}

export function formatCurrency(decimals: number, value: BigNumber | string, precision?: number): string {
  let stringified = value.toString()
  const negative = stringified.startsWith('-')
  if (negative) {
    stringified = stringified.substring(1)
  }

  let decimalPart =
    stringified.length <= decimals
      ? stringified.padStart(decimals, '0')
      : stringified.substring(stringified.length - decimals)
  decimalPart = stripEndZeroes(decimalPart)

  let integerPart = stringified.length <= decimals ? '0' : stringified.substring(0, stringified.length - decimals)
  integerPart = applyCommaSeparator(integerPart)

  const uncut = decimalPart !== '' ? `${integerPart}.${decimalPart}` : integerPart
  return `${negative ? '-' : ''}${cutToPrecision(uncut, precision ?? decimals)}`
}

function applyCommaSeparator(value: string) {
  const commaCount = value.length / 3
  const resultValue = value.split('')
  for (let i = 1; i < commaCount; i++) {
    resultValue.splice(-4 * i + 1, 0, ',')
  }
  return resultValue.join('')
}

function stripEndZeroes(value: string) {
  return value.replace(/0+$/, '')
}

function cutToPrecision(value: string, precision?: number) {
  if (!precision && precision !== 0) {
    return value
  }

  const decimalIndex = value.indexOf('.')
  if (decimalIndex < 0) {
    return value
  }

  return value.substring(0, decimalIndex + precision + 1)
}
